import React, { useEffect, useMemo, useRef, useState } from "react";
import { toolCallStore, ToolProgressBanner } from "./components/tool-progress-barnner/tool-progress-banner";
import { StriimIconButton, StriimTypography, StriimTooltip } from "@striim/striim-ui";
import { Box, Grid, SvgIcon } from "@mui/material";
import { CopilotKit } from "@copilotkit/react-core";
import { CopilotChat } from "@copilotkit/react-ui";
import { ErrorBoundary } from "react-error-boundary";
// @ts-ignore
import "@copilotkit/react-ui/styles.css";
// @ts-ignore
import "./striim-ai-sidebar.css";
import {
    AIAssistantGradient,
    AIAssistant,
    Settings,
    SidebarCollapse,
    Close,
    ChevronLeft
} from "../../generic/icon/customIcons";
import ConnectionLost from "app/images/connection_lost.svg";
// @ts-ignore
import { useAgent, useDefaultRenderTool } from "@copilotkit/react-core/v2";
import { useCopilotChat, useCopilotChatSuggestions } from "@copilotkit/react-core";
import { useContextualSuggestions } from "./hooks/use-contextual-suggestions";
import api from "../../../core/api/api";
import securityService from "core/services/securityService/securityService";
import utils from "core/utils";
import CustomUserMessage from "./components/custom-user-message/custom-user-message";
import { useStriimAITools } from "./tools";
import CustomInput from "./components/custom-input/custom-input";
import CustomAssistantMessage from "./components/custom-assistant-message/custom-assistant-message";
import ChatErrorFallback from "./components/chat-error-fallback/chat-error-fallback";
import styles, {
    AILinearProgress,
    settingsIconWrap,
    sidebarContent,
    sidebarToggleIcon
} from "./striim-ai-sidebar.styles";
import AiGradientButton from "./components/ai-gradient-button/ai-gradient-button";
import { useStriimAIConfig } from "./hooks/use-striim-ai-config";
import { useAppDispatch, useAppSelector } from "src/stores/redux/hooks";
import App from "app";
import {
    clearConfiguration,
    selectIsEnabled,
    selectIsHealthy,
    selectIsLoading,
    setIsLoadingAction,
    selectIsSidebarMaximized,
    setSidebarOpenAction,
    toggleSidebarMaximizedAction,
    toggleSidebarOpenAction,
    setConfigFormData,
    selectIsConfigured,
    setIsInitEndpointAccessibleAction,
    selectIsInitEndpointAccessible,
    selectAuthHeaders,
    selectAreAuthHeadersReady
} from "src/stores/redux/features/striim-ai/striim-ai.slice";
import AIStatusIndicator from "./components/ai-status-indicator/ai-status-indicator";
import AIConfigScreen from "./components/ai-config-screen/ai-config-screen";
import { ExistingServerConfig, NewServerConfig } from "./components/ai-config-screen/ai-config-screen";
import { AI_AGENT_NAME } from "./ai-constants";
import AILoadingScreen from "./components/ai-loading-screen/ai-loading-screen";
import growl from "app/components/common/growl";
import copilotService, { getStriimAIDemoUrl, isInitEndpointAccessible } from "core/services/copilotService";
import {
    getErrorResult,
    growlStriimAiConnectionError,
    markConnectionErrorShown,
    wasConnectionErrorShown
} from "./utils/health-check.utils";
import { getConfigState, configStateToProps } from "./utils/config-state.utils";

const NEW_SERVER_HEALTH_POLL_MAX_TRIES = 120;
const NEW_SERVER_HEALTH_POLL_INTERVAL_MS = 5_000;
const HEALTH_POLL_INTERVAL_MS = 30_000;

interface StriimAISidebarProps {
    iconClassName?: string;
}

interface StriimAISidebarContentProps {
    onLoadingChange?: (isLoading: boolean) => void;
    staticSuggestions: { title: string; message: string }[];
}

// Rendered by useDefaultRenderTool for every tool call in the stream.
// Returns null (no in-stream UI) but registers the tool name so ToolProgressBanner can count it.
// ToolProgressBanner lives in CustomChatInput — outside CopilotKit's message stream — so it
// is never unmounted when a response completes.
const ToolCallTracker: React.FC<{ name: string; status: string }> = ({ name, status }) => {
    useEffect(() => {
        toolCallStore.register(name);
    }, [name, status]);
    return null;
};

const StriimAISidebarContent: React.FC<StriimAISidebarContentProps> = ({
    onLoadingChange,
    staticSuggestions,
    version
}) => {
    useStriimAITools();

    useCopilotChatSuggestions({ available: "always", suggestions: staticSuggestions }, [staticSuggestions]);

    const { agent } = useAgent({ agentId: "striim_ai" });

    const credentialsRef = useRef({
        user_id: App.user?.name,
        user_token: api.token,
        striim_version: version
    });
    useEffect(() => {
        credentialsRef.current = {
            user_id: App.user?.name,
            user_token: api.token,
            striim_version: version
        };
    }, [App.user?.name, api.token, version]);

    const middlewareInstalled = useRef(false);
    useEffect(() => {
        if (agent && !middlewareInstalled.current) {
            agent.use({
                run(input: any, next: any) {
                    input.state = {
                        ...input.state,
                        user_id: credentialsRef.current.user_id,
                        user_token: credentialsRef.current.user_token,
                        striim_version: credentialsRef.current.striim_version
                    };
                    return next.run(input);
                }
            });
            middlewareInstalled.current = true;
        }
    }, [agent]);

    const { isLoading } = useCopilotChat();

    const prevLoadingRef = useRef(false);
    const endClusterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Tracks whether the current request has already triggered a resetCluster, so oscillations
    // (isLoading going false→true multiple times mid-response) don't create extra generations.
    const didResetRef = useRef(false);
    useEffect(() => {
        if (isLoading && !prevLoadingRef.current) {
            if (endClusterTimerRef.current) clearTimeout(endClusterTimerRef.current);
            if (!didResetRef.current) {
                toolCallStore.resetCluster();
                didResetRef.current = true;
            }
        } else if (!isLoading && prevLoadingRef.current) {
            endClusterTimerRef.current = setTimeout(() => {
                toolCallStore.endCluster();
                didResetRef.current = false; // allow next user message to reset
            }, 500);
        }
        prevLoadingRef.current = isLoading;
    }, [isLoading]);

    useDefaultRenderTool({
        render: ({ name, status }: { name: string; status: string }) => (
            <ToolCallTracker name={name} status={String(status)} />
        )
    });

    useEffect(() => {
        onLoadingChange?.(isLoading);
    }, [isLoading, onLoadingChange]);

    return (
        <>
            <ToolProgressBanner />
            <CopilotChat
                labels={{
                    title: `Ask ${AI_AGENT_NAME}`,
                    placeholder: "Ask me about striim",
                    initial: `👋 Hi! I'm ${AI_AGENT_NAME}, your Striim AI assistant. I can help you manage and monitor your Striim resources, including applications, sources, targets, streams, and more.\n\nI can make mistakes. Please verify before executing commands. \n\n How can I assist you today?`
                }}
                UserMessage={CustomUserMessage}
                Input={CustomInput}
                AssistantMessage={CustomAssistantMessage}
            />
        </>
    );
};

// Panel rendered at frame level as a flex sibling of #page-content
export const StriimAISidebarPanel: React.FC = () => {
    const dispatch = useAppDispatch();
    const rawSuggestions = useContextualSuggestions();
    const suggestions = React.useMemo(() => rawSuggestions, [JSON.stringify(rawSuggestions)]);
    const isMaximized = useAppSelector(selectIsSidebarMaximized);
    const [isChatLoading, setIsChatLoading] = useState(false);
    const {
        isConfigured,
        copilotUrl,
        isLoading,
        healthStatus,
        recheckConfiguration,
        pollUntilHealthy
    } = useStriimAIConfig();

    const isInitEndpointAccessibleState = useAppSelector(selectIsInitEndpointAccessible);
    const [isLaunching, setIsLaunching] = useState(false);
    const [isNewServerLaunch, setIsNewServerLaunch] = useState(false);
    const [isShowingConfig, setIsShowingConfig] = useState(false);

    const STRIIM_AI_DEMO_SERVER_URL = useMemo(() => getStriimAIDemoUrl(), []);

    const [isDemoServerHealthy, setIsDemoServerHealthy] = useState(true);
    const authHeaders = useAppSelector(selectAuthHeaders);
    const areAuthHeadersReady = useAppSelector(selectAreAuthHeadersReady);

    const configState = getConfigState({
        isConfigured: !!isConfigured,
        healthStatus,
        isLoading,
        isInitEndpointAccessible: isInitEndpointAccessibleState
    });
    const { isHealthy, isMisconfigured, isError } = configStateToProps(configState);

    useEffect(() => {
        dispatch(setIsInitEndpointAccessibleAction(isInitEndpointAccessible()));
    }, [healthStatus, isConfigured, isLoading, dispatch]);

    // Only relevant while the config screen (with its "Try Striim AI's servers" option and the
    // new-server provider dropdown) is actually visible — must not run on every page load, and
    // must not affect the shared init-endpoint-accessible status used by the main connection indicator.
    useEffect(() => {
        if (isConfigured && !isShowingConfig) return;

        let cancelled = false;
        copilotService.fetchProviderConfig();
        const checkDemoServerHealth = async () => {
            const healthStatus = await copilotService.fetchHealthStatus(STRIIM_AI_DEMO_SERVER_URL, false);
            if (!cancelled) setIsDemoServerHealthy(healthStatus !== null);
        };
        checkDemoServerHealth();
        return () => {
            cancelled = true;
        };
    }, [isConfigured, isShowingConfig, STRIIM_AI_DEMO_SERVER_URL]);

    const handleConnect = async (config: ExistingServerConfig) => {
        setIsLaunching(true);
        dispatch(setIsLoadingAction(true));
        try {
            dispatch(clearConfiguration());
            await api.configureCopilot(config.serverUrl, "", "", "", "", "existing");
            await recheckConfiguration();
            const healthStatus = await pollUntilHealthy(60);
            const errorResult = getErrorResult(healthStatus);
            if (!errorResult) {
                setIsShowingConfig(false);
            } else {
                growlStriimAiConnectionError(errorResult);
            }
            dispatch(setConfigFormData(null));
        } catch (e) {
            growl.error(
                e?.message ??
                    `There was a problem connecting to ${AI_AGENT_NAME}'s server. Please check the URL and try again.`,
                `Error connecting to ${AI_AGENT_NAME}`
            );
        } finally {
            setIsLaunching(false);
            dispatch(setIsLoadingAction(false));
        }
    };

    const handleTryStriimAIServers = () => handleConnect({ serverUrl: STRIIM_AI_DEMO_SERVER_URL });

    const handleLaunch = async (config: NewServerConfig) => {
        setIsLaunching(true);
        setIsNewServerLaunch(true);
        dispatch(setIsLoadingAction(true));
        try {
            dispatch(clearConfiguration());
            await api.configureCopilot(
                window.location.protocol + "//" + window.location.hostname + ":9090/do/ai",
                config.provider,
                config.model ?? "",
                config.apiKey ?? "",
                config.ollamaUrl ?? "",
                "LOCAL"
            );
            await api.stopCopilot();
            await utils.wait(5);
            await api.launchCopilot();
            await recheckConfiguration();

            const healthStatus = await pollUntilHealthy();
            const errorResult = getErrorResult(healthStatus);
            if (!errorResult) {
                setIsShowingConfig(false);
            } else {
                growlStriimAiConnectionError(errorResult);
            }
            dispatch(setConfigFormData(null));
        } catch (e) {
            growl.error(e?.message ?? e, `Error starting ${AI_AGENT_NAME}`);
        } finally {
            setIsLaunching(false);
            setIsNewServerLaunch(false);
            dispatch(setIsLoadingAction(false));
        }
    };
    const isSaaSEnv = useMemo(() => {
        return securityService.isSaaSEnvironment();
    }, []);

    const content = (
        <Box sx={sidebarContent(isMaximized)} data-striim-ai-sidebar>
            {(isLoading || isLaunching) && (
                <AILoadingScreen
                    {...(isNewServerLaunch && {
                        message: `Launching ${AI_AGENT_NAME}`,
                        subMessage: `This may take up to 10 minutes on the first launch while ${AI_AGENT_NAME} finishes setting up.`
                    })}
                />
            )}
            {!isLoading &&
                !isLaunching &&
                (!isConfigured || isShowingConfig) &&
                (isSaaSEnv ? (
                    <Box sx={styles.saasDisabledState}>
                        <ConnectionLost />
                        <StriimTypography variant="h3" sx={styles.saasDisabledCenteredText}>
                            {AI_AGENT_NAME} is not configured
                        </StriimTypography>
                        <StriimTypography variant="body4" sx={styles.saasDisabledCenteredText} color="greyscale.800">
                            Ask a SaaS Account Admin or Service Admin to enable and configure AI providers for this
                            service
                        </StriimTypography>
                    </Box>
                ) : (
                    <AIConfigScreen
                        isConfigured={!!isConfigured}
                        isHealthy={isHealthy}
                        isMisconfigured={isMisconfigured}
                        isError={isError}
                        healthStatus={healthStatus}
                        isDemoServerHealthy={isDemoServerHealthy}
                        onConnect={handleConnect}
                        onLaunch={handleLaunch}
                        onTryStriimAIServers={handleTryStriimAIServers}
                    />
                ))}
            {!isLoading && !isLaunching && isConfigured && !isShowingConfig && (
                <ErrorBoundary FallbackComponent={ChatErrorFallback}>
                    <StriimAISidebarContent staticSuggestions={suggestions} onLoadingChange={setIsChatLoading} />
                </ErrorBoundary>
            )}
        </Box>
    );

    const keyToRender = copilotUrl + isHealthy.toString();

    const body = !copilotUrl ? (
        content
    ) : !areAuthHeadersReady ? (
        <AILoadingScreen />
    ) : (
        <CopilotKit
            key={keyToRender}
            runtimeUrl={copilotUrl}
            headers={authHeaders ?? {}}
            agent="striim_ai"
            enableInspector={false}
            showDevConsole={false}
        >
            {content}
        </CopilotKit>
    );

    return (
        <>
            <Grid container sx={styles.copilotHeader}>
                <Grid container sx={styles.headerIconRow}>
                    <SvgIcon component={AIAssistant as React.ElementType} sx={styles.sidebarAIIcon} />
                    <StriimTypography sx={styles.copilotHeaderTypography} variant="h3" color="greyscale.900">
                        Ask {AI_AGENT_NAME}
                    </StriimTypography>
                </Grid>
                <Box sx={styles.headerActions}>
                    {isConfigured && <AIStatusIndicator healthStatus={healthStatus} />}
                    {isConfigured && securityService.isAdmin() && !securityService.isSaaSEnvironment() && (
                        <StriimTooltip title={isShowingConfig ? "Back to chat" : "Settings"} placement="bottom">
                            <Box component="span" sx={settingsIconWrap(isShowingConfig)}>
                                <StriimIconButton
                                    color={"greyscale.900" as any}
                                    onClick={() => setIsShowingConfig(v => !v)}
                                    data-test-id="striim-ai-settings"
                                    aria-label={isShowingConfig ? "Back to chat" : "Settings"}
                                >
                                    <SvgIcon
                                        component={(isShowingConfig ? ChevronLeft : Settings) as React.ElementType}
                                    />
                                </StriimIconButton>
                            </Box>
                        </StriimTooltip>
                    )}
                    <StriimIconButton
                        color="greyscale.900"
                        onClick={() => dispatch(toggleSidebarMaximizedAction())}
                        data-test-id="maximize-striim-ai"
                    >
                        <SvgIcon component={SidebarCollapse as React.ElementType} sx={sidebarToggleIcon(isMaximized)} />
                    </StriimIconButton>
                    <StriimIconButton
                        color={"greyscale.900" as any}
                        onClick={() => dispatch(setSidebarOpenAction(false))}
                        data-test-id="close-striim-ai"
                    >
                        <SvgIcon component={Close as React.ElementType} />
                    </StriimIconButton>
                </Box>
            </Grid>
            {isChatLoading && isConfigured && <AILinearProgress />}
            {body}
        </>
    );
};

// Toggle button rendered in the top toolbar
const StriimAISidebar: React.FC<StriimAISidebarProps> = ({ iconClassName }) => {
    const dispatch = useAppDispatch();
    const isEnabled = useAppSelector(selectIsEnabled);
    const isConfigured = useAppSelector(selectIsConfigured);
    const areAuthHeadersReady = useAppSelector(selectAreAuthHeadersReady);
    const pollIntervalRef = useRef<ReturnType<typeof setInterval>>();
    const handleToggleSidebar = () => {
        dispatch(toggleSidebarOpenAction());
    };

    useEffect(() => {
        if (!isEnabled) {
            return;
        }
        pollIntervalRef.current = setInterval(async () => {
            const pollingUntilHealthy = copilotService.isPollingUntilHealthy;
            if (pollingUntilHealthy) return;
            const healthStatus = await copilotService.pollHealth();
            if (!isConfigured) return;
            const errorResult = getErrorResult(healthStatus);
            if (errorResult) {
                if (!wasConnectionErrorShown()) {
                    growlStriimAiConnectionError(errorResult);
                    markConnectionErrorShown();
                }
            }
        }, HEALTH_POLL_INTERVAL_MS);
        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
        };
    }, [isConfigured]);

    // Hidden entirely when copilot is not enabled; disabled until init (config + auth headers) completes.
    if (!isEnabled) {
        return null;
    }

    const isChatReady = !isConfigured || areAuthHeadersReady;

    return (
        <AiGradientButton
            icon={<SvgIcon component={AIAssistantGradient as React.ElementType} sx={styles.toolbarAIIcon} />}
            label={`Ask ${AI_AGENT_NAME}`}
            onClick={handleToggleSidebar}
            disabled={!isChatReady}
        />
    );
};

export default StriimAISidebar;
